import React, { useEffect, useRef, useState } from 'react';
import _, { clone } from 'lodash';
import { useSetRecoilState } from 'recoil';
import { css } from '@emotion/react';
import { Input, PrimaryButton, Select } from '@components/atoms';
import { dialogContentsState } from '@components/templates/Dialog';
import { getFetcher, getFetcherFormData } from '../../../../App';
import useStaticDialog from '../../../../utils/hooks/useStaticDialog';
import SettingCommonMethods from '../../../../utils/settingCommon';
import {
  SettingBox,
  SettingList,
  SettingWrapper,
  StyledReportWrap,
  SwButtonSection,
  SelectBox,
  StyledReportIndexOption,
  TwoRowInputBox,
  RowInput,
  RowDivider,
  StyledSettingLabel,
  StyledImageUpload,
  StyledUploadInfo,
  StyledTextInfo,
} from './style';
import { submitDebounce } from 'utils/lib';
import he from 'he';

interface optionDataType {
  [key: string]: boolean | string;
}

const ADOption = () => {
  const ref = useRef<any>(null);
  const inputData = ['reportGeneral', 'reportAgeRelated', 'reportSeizure', 'reportVolumeThickness', 'reportSummary'];
  const { submitConfirmDialog } = useStaticDialog();
  const { errorHandler } = SettingCommonMethods();
  const setDialogContents = useSetRecoilState(dialogContentsState);

  const [imageAxes, setImageAxes] = useState<'width' | 'height'>('width');
  const [tempImage, setTempImage] = useState<File | null>(null);

  const [data, setData] = useState<optionDataType>({
    thickness: true,
    thicknessWeb: true,
    thicknessTimeout: 'THREE',
    reportGeneral: true,
    reportAgeRelated: true,
    reportSeizure: true,
    reportVolumeThickness: true,
    reportSummary: true,
    companyDisplayType: '',
    logoFile: '',
    displayCompanyName1: '',
    displayCompanyName2: '',
  });

  const [backupData, setBackupData] = useState<optionDataType>({
    thickness: true,
    thicknessWeb: true,
    thicknessTimeout: 'THREE',
    reportGeneral: true,
    reportAgeRelated: true,
    reportSeizure: true,
    reportVolumeThickness: true,
    reportSummary: true,
    companyDisplayType: '',
    logoFile: '',
    displayCompanyName1: '',
    displayCompanyName2: '',
  });

  const analysisOption = [
    { label: 'Vol,Thk', value: 'true' },
    { label: 'Volume', value: 'false' },
  ];

  const thicknessTimeOutOption = [
    { label: '3', value: 'THREE' },
    { label: '10', value: 'TEN' },
    { label: '20', value: 'TWENTY' },
  ];

  /**
   * 노출 text 치환
   * @param text
   */
  const replaceInputText = (text: string) => {
    if (text === 'reportVolumeThickness') return 'Volume(4p)/Thickness Report(2p)';
    return text.replace('report', '');
  };

  /**
   * 사용자 가 값 변경시 동작
   * @param key
   * @param value
   */
  const setDataValue = (key: string, value: boolean | string) => {
    setData((prevState) => {
      let _clone = clone(prevState);
      _clone[key as string] = value;
      return _clone;
    });
  };

  /**
   * submit confirm cb
   * data 전송
   */
  const postDataCB = async () => {
    const url = 'api/setting/ad/option'; // ok
    const token = localStorage.getItem('PROD_ADMIN_ACCESS_TOKEN') || undefined;

    const fd = new FormData();

    const {
      thickness,
      thicknessWeb,
      thicknessTimeout,
      reportGeneral,
      reportAgeRelated,
      reportSeizure,
      reportVolumeThickness,
      reportSummary,
      companyDisplayType,
      logoFile,
      displayCompanyName1,
      displayCompanyName2,
    } = data;

    fd.append('thickness', String(thickness));
    fd.append('thicknessWeb', String(thicknessWeb));
    fd.append('thicknessTimeout', thicknessTimeout as string);
    fd.append('reportGeneral', String(reportGeneral));
    fd.append('reportAgeRelated', String(reportAgeRelated));
    fd.append('reportSeizure', String(reportSeizure));
    fd.append('reportVolumeThickness', String(reportVolumeThickness));
    fd.append('reportSummary', String(reportSummary));
    if (companyDisplayType) {
      fd.append('companyDisplayType', companyDisplayType as string);
    }
    fd.append('displayCompanyName1', displayCompanyName1 as string);
    fd.append('displayCompanyName2', displayCompanyName2 as string);

    if (tempImage) {
      fd.append('logoFile', tempImage);
    }

    await getFetcherFormData({
      method: 'POST',
      accessToken: token,
      body: fd,
    })(url)
      .then((r) => {
        if (r.code === '200') {
          setDialogContents({
            type: 'plain',
            body: `It has been reflected.`,
            footer: (
              <PrimaryButton
                onClick={async () => {
                  setDialogContents({ type: 'hidden' });
                }}
              >
                OK
              </PrimaryButton>
            ),
          });
          setBackupData(data);
        } else {
          errorHandler(r);
        }
      })
      .catch((e) => {
        console.log(e, 'e');
      });
  };

  const loadImage = (src: string) => {
    return new Promise<HTMLImageElement | null>((resolve) => {
      const image = new Image();

      image.onload = () => {
        resolve(image);
      };

      image.onerror = () => {
        resolve(null);
      };

      image.src = src;
    });
  };

  /**
   * get ad option data
   */
  const getData = async () => {
    const url = 'api/setting/ad/option'; // ok
    const token = localStorage.getItem('PROD_ADMIN_ACCESS_TOKEN') || undefined;

    await getFetcher({
      method: 'GET',
      accessToken: token,
    })(url)
      .then(async (r) => {
        if (r.code === '200') {
          // 이미지 경로 decoding

          const imageURL = decodeURIComponent(r.result.logoFile);
          const image = await loadImage(imageURL);

          if (image) {
            resize(image);
            r.result.logoFile = imageURL;
          } else {
            r.result.logoFile = '';
          }

          setData(r.result);
          setBackupData(r.result);
        } else {
          errorHandler(r);
        }
      })
      .catch((e) => {
        console.log(e, 'e');
      });
  };

  /**
   *
   * @param ev
   * hospitalName 입력
   */
  const hospitalNameHandler = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const value = ev.target.value;
    const name = ev.target.name;

    // 최대 15자까지 입력 가능
    if (value.length > 15) return;

    const key = name === 'first-line-text' ? 'displayCompanyName1' : 'displayCompanyName2';

    setData((prevState) => {
      return { ...prevState, [key]: value };
    });
  };

  /**
   *
   * @param ev
   */
  const displayTypeHandler = (ev: React.ChangeEvent<HTMLInputElement>) => {
    setData((prevState) => {
      return { ...prevState, companyDisplayType: ev.target.value };
    });
  };

  /**
   * 이미지 change
   */
  const imageHandler = async (ev: React.ChangeEvent<HTMLInputElement>) => {
    const allowType = ['image/jpg', 'image/png', 'image/jpeg'];
    const files = ev.target.files;

    if (!files || files.length === 0) return;

    const file = files[0];

    if (!allowType.includes(file.type)) {
      setDialogContents({
        type: 'plain',
        body: `Please upload a JPG or PNG image.`,
        footer: (
          <PrimaryButton
            onClick={async () => {
              setDialogContents({ type: 'hidden' });
            }}
          >
            OK
          </PrimaryButton>
        ),
      });
      return;
    }

    if (file.size > 500 * 1024) {
      setDialogContents({
        type: 'plain',
        body: `File size exceeds 500KB. Please reduce the size and try again.`,
        footer: (
          <PrimaryButton
            onClick={async () => {
              setDialogContents({ type: 'hidden' });
            }}
          >
            OK
          </PrimaryButton>
        ),
      });
      return;
    }

    const tempURL = URL.createObjectURL(file);
    const image = await loadImage(tempURL);

    if (image) {
      resize(image);
      setTempImage(file);
      setData((prevState) => ({
        ...prevState,
        logoFile: URL.createObjectURL(file),
      }));
    }
  };

  const resize = (image: HTMLImageElement) => {
    const ratio = 200 / image.naturalWidth;
    const resizeHeight = ratio * image.naturalHeight;

    if (resizeHeight > 50) {
      setImageAxes('height');
    } else {
      setImageAxes('width');
    }
  };

  useEffect(() => {
    (async () => {
      await getData();
    })();
  }, []);

  return (
    <>
      <label>Option</label>
      <StyledReportWrap>
        <SettingWrapper>
          <strong>PACS analysis option</strong>
          <SettingBox>
            <SelectBox>
              <Select
                key={`${data.thickness}`}
                styles={css`
                  width: 140px;
                `}
                type={'thickness'}
                list={analysisOption}
                selected={data.thickness.toString()}
                onSelected={(e) => setDataValue('thickness', e.value === 'true')}
              />
            </SelectBox>
          </SettingBox>
        </SettingWrapper>
        <SettingWrapper>
          <strong>Web analysis option</strong>
          <SettingBox>
            <SelectBox>
              <Select
                key={`${data.thicknessWeb}`}
                synchronize
                styles={css`
                  width: 140px;
                `}
                type={'thicknessWeb'}
                list={analysisOption}
                selected={data.thicknessWeb.toString()}
                onSelected={(e) => setDataValue('thicknessWeb', e.value === 'true')}
              />
            </SelectBox>
          </SettingBox>
        </SettingWrapper>

        <SettingWrapper>
          <strong>Thickness timeout</strong>
          <SettingBox>
            <SettingList>
              <li>
                <SelectBox>
                  <Select
                    key={`${data.thicknessTimeout}`}
                    synchronize
                    styles={css`
                      width: 92x;
                    `}
                    type={'thicknessTimeout'}
                    list={thicknessTimeOutOption}
                    selected={data.thicknessTimeout.toString()}
                    onSelected={(e) => setDataValue('thicknessTimeout', e.value)}
                  />
                </SelectBox>
                <span className={'selectbox-text'}>hours</span>
              </li>
              <li className={'guide-text'}>Analysis fails when a set timeout occurs.</li>
            </SettingList>
          </SettingBox>
        </SettingWrapper>

        <SettingWrapper>
          <strong>Report Index option</strong>
          <SettingBox>
            <SettingList>
              <li>
                <StyledReportIndexOption>
                  {inputData.map((inputType, index) => {
                    return (
                      <li key={index}>
                        <Input
                          type={'checkbox'}
                          name={inputType}
                          checked={(data[`${inputType}`] as boolean) || false}
                          disabled={index === 0}
                          onChange={(e) => setDataValue(inputType, e.target.checked)}
                        >
                          {replaceInputText(inputType)}
                        </Input>
                      </li>
                    );
                  })}
                </StyledReportIndexOption>
              </li>
              <li className={'guide-text'}>The selected index is displayed in the Heuron AD Expert report.</li>
            </SettingList>
          </SettingBox>
        </SettingWrapper>

        <SettingWrapper>
          <strong>Hospital name (Report)</strong>
          <SettingBox>
            <div>
              <SettingList>
                <li>
                  <StyledSettingLabel>
                    <Input
                      type={'radio'}
                      name={`displayHospital`}
                      checked={data.companyDisplayType === 'TEXT'}
                      value={'TEXT'}
                      onChange={displayTypeHandler}
                    >
                      Text
                    </Input>
                  </StyledSettingLabel>
                  <TwoRowInputBox className={data.companyDisplayType !== 'TEXT' ? 'disabled' : ''}>
                    <RowInput
                      disabled={data.companyDisplayType !== 'TEXT'}
                      value={he.decode(data.displayCompanyName1 as string)}
                      name="first-line-text"
                      placeholder="1st line text (max 15 characters)"
                      onChange={hospitalNameHandler}
                    />
                    <RowDivider />
                    <RowInput
                      disabled={data.companyDisplayType !== 'TEXT'}
                      value={he.decode(data.displayCompanyName2 as string)}
                      name="second-line-text"
                      placeholder="2nd line text (max 15 characters)"
                      onChange={hospitalNameHandler}
                    />
                  </TwoRowInputBox>

                  <StyledTextInfo>
                    <div>If blank, Heuron logo will display.</div>
                    <div>If you want to write without line breaks, enter text only in the first field.</div>
                  </StyledTextInfo>
                </li>
                <li>
                  <StyledSettingLabel>
                    <Input
                      type={'radio'}
                      name={`displayHospital`}
                      checked={data.companyDisplayType === 'IMAGE'}
                      value={'IMAGE'}
                      onChange={displayTypeHandler}
                    >
                      Image(Logo)
                    </Input>
                  </StyledSettingLabel>
                  <div>
                    <StyledImageUpload
                      className={[
                        data.logoFile === '' ? 'empty' : imageAxes === 'width' ? 'f-width' : 'f-height',
                        data.companyDisplayType !== 'IMAGE' ? 'disabeld' : '',
                      ].join(' ')}
                      onClick={() => {
                        ref.current.click();
                      }}
                    >
                      {data.logoFile ? <img src={data.logoFile as string} /> : <div>Image Upload</div>}

                      {/* input */}
                      <input
                        type="file"
                        disabled={data.companyDisplayType !== 'IMAGE'}
                        ref={ref}
                        onChange={imageHandler}
                      />
                    </StyledImageUpload>

                    <StyledUploadInfo>
                      <li>170x24px(JPG or PNG), max size 500KB</li>
                      <li>If blank, Heuron logo will display</li>
                    </StyledUploadInfo>
                  </div>
                </li>
              </SettingList>
            </div>
          </SettingBox>
        </SettingWrapper>
      </StyledReportWrap>
      <SwButtonSection>
        <PrimaryButton
          styles={css`
            width: 145px;
            height: 48px;
            font-size: 18px;
          `}
          onClick={submitDebounce(() => submitConfirmDialog(postDataCB))}
          disabled={_.isEqual(data, backupData)}
        >
          Submit
        </PrimaryButton>
      </SwButtonSection>
    </>
  );
};

export default ADOption;
